import {generateDefaultModel} from '@github-ui/copilot-chat/utils/models'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useSearchParams} from '@github-ui/use-navigate'
import {useEffect, useRef, useState} from 'react'

import LoggedOutView from '../components/LoggedOutView/LoggedOutView'
import type {ChatContentV2Props} from './../components/ChatContentV2/ChatContentV2'
import {CopilotContext} from './../contexts/CopilotContext'
import {useInitialCopilotContext} from './../hooks/useInitialCopilotContext'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CopilotImmersiveLoggedOutProps {}

export const defaultProps: Partial<CopilotImmersiveLoggedOutProps> = {}

const CopilotImmersiveLoggedOut = (props: CopilotImmersiveLoggedOutProps) => {
  const initializedProps: CopilotImmersiveLoggedOutProps = {...defaultProps, ...props}
  // eslint-disable-next-line no-empty-pattern
  const {} = initializedProps

  const [searchParams, setSearchParams] = useSearchParams()
  const defaultModel = generateDefaultModel()
  const [initialModelId, setInitialModelId] = useState<string | null>(null)
  const autoPopulated = useRef(false)

  const {copilotContextValue} = useInitialCopilotContext({defaultModel})
  const isHyperspace2025LoggedOutBatch2 = isFeatureEnabled('hyperspace_2025_logged_out_batch_2')

  useEffect(() => {
    if (autoPopulated.current) return
    autoPopulated.current = true

    const queryPrompt = searchParams.get('prompt')
    if (queryPrompt) copilotContextValue.setPrompt(queryPrompt)

    if (isHyperspace2025LoggedOutBatch2) {
      // TODO: Update once CAPI returns model picker data
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitialModelId('gpt-4.1')
    } else {
      const queryModelId = searchParams.get('model')
      setInitialModelId(queryModelId)
    }

    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('prompt')
    newSearchParams.delete('model')
    setSearchParams(newSearchParams, {replace: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams])

  return (
    <CopilotContext value={copilotContextValue}>
      <LoggedOutView
        // TODO: Remove typecasting once CAPI returns model picker data
        initialModelId={initialModelId as ChatContentV2Props['initialModelId']}
        onIdeListItemClick={(_, ideName) => {
          sendEvent('click', {
            category: 'copilot_loggedOut',
            action: 'ide_option_click',
            label: 'copilot_loggedOut',
            ideName,
          })
        }}
        onProductAnnouncementBannerEvent={event => {
          switch (event) {
            case 'displayed':
              sendEvent('view', {
                category: 'universe_banner',
                action: 'banner_view',
                label: 'banner_universe_loggedOut',
              })
              break
            case 'clicked':
              sendEvent('click', {
                category: 'universe_banner',
                action: 'banner_click_openModal',
                label: 'banner_universe_loggedOut',
              })
              break
            case 'dismissed':
              sendEvent('click', {
                category: 'universe_banner',
                action: 'banner_dismiss',
                label: 'banner_universe_loggedOut',
              })
              break
          }
        }}
        onProductAnnouncementModalEvent={(event, data) => {
          switch (event) {
            case 'opened':
              sendEvent('view', {
                category: 'universe_modal',
                action: 'modal_view',
                label: 'modal_universe_loggedOut',
              })
              break
            case 'closed':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_close',
                label: 'modal_universe_loggedOut',
              })
              break
            case 'tab_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_nav_click',
                label: 'modal_universe_loggedOut',
                navItem: data?.tabName as string,
              })
              break
            case 'main_cta1_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_primary_cta_left_click',
                label: 'modal_universe_loggedOut',
              })
              break
            case 'see_all_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_seeAllAnnouncements_left_click',
                label: 'modal_universe_loggedOut',
              })
              break
            case 'tab_cta1_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_primary_cta_right_click',
                label: 'modal_universe_loggedOut',
                navItem: data?.tabName as string,
                ctaType: 'primary_right',
                ctaUrl: data?.url as string,
                userStatus: 'logged_out',
                positionIndex: data?.tabIndex as number,
              })
              break
            case 'tab_cta2_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_secondary_cta_right_click',
                label: 'modal_universe_loggedOut',
                navItem: data?.tabName as string,
                ctaType: 'secondary_right',
                ctaUrl: data?.url as string,
                userStatus: 'logged_out',
                positionIndex: data?.tabIndex as number,
              })
              break
            case 'tab_link_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_link_right_click',
                label: 'modal_universe_loggedOut',
                navItem: data?.tabName as string,
                ctaType: 'learn_more_right',
                ctaUrl: data?.url as string,
                userStatus: 'logged_out',
                positionIndex: data?.tabIndex as number,
              })
              break
            case 'tab_description_link_clicked':
              sendEvent('click', {
                category: 'universe_modal',
                action: 'modal_description_link_right_click',
                label: 'modal_universe_loggedOut',
                navItem: data?.tabName as string,
                ctaType: 'link',
                ctaUrl: data?.url as string,
                userStatus: 'logged_out',
                positionIndex: data?.tabIndex as number,
              })
              break
          }
        }}
      />
    </CopilotContext>
  )
}

export default CopilotImmersiveLoggedOut
